import { Box, Button, Flex, Grid, GridItem, Icon, Spinner, useToast, useTranslation } from "@familyzone/component-library";
import React, { useContext, useEffect, useState } from "react";
import { InjectedRouter } from "react-router";
import StudentNamePill from "../../../modules/StudentNamePill";
import PageWithHeader from "../../templates/PageWithHeader";
import EventTimelineCard from "./EventTimelineCard";
import FactsCardSet from "./FactsCardSet";
import GraphCard from "./GraphCard";
import RedFlagsCard from "./RedFlagsCard";
import TopTrafficTypesCard from "./TopTrafficTypesCard";
import { useDateRangeFilterStore } from "../../../storez/DateRangeFilterStore";
import GlobalDatePickerVisibilityActions from "../../../actions/GlobalDatePickerVisibilityActions";
import FindUserModal from "../../modals/FindUserModal";
import { ApiDates } from "../../../utils/DateTimeUtil";
import { ReactRouterCompatContext } from "../../ContextProvider";
import { UserUMS } from "../../../types/Users";
import { shallow } from "zustand/shallow";
import { useReportingUserStore } from "../../../storez/ReportingUserStore";
import { ResponseError } from "../../../types/Api";

interface IUserDashboardIndexProps {
  location?: {
    query?: ApiDates;
  };
  params: {
    // Qoria user ID (UUID) or username
    user: string;
  };
}

const UserDashboardIndex = ({ location, params }: IUserDashboardIndexProps): JSX.Element => {
  const { router } = useContext(ReactRouterCompatContext) as { router: InjectedRouter };
  const { t } = useTranslation();
  const { errorToast } = useToast();

  // by default, value change is detected by comparing object references but for generated
  // values it is always different on every render and would cause everything to reload;
  // the 'shallow' comparator checks whether object keys/values have actually changed
  const apiDateOverride = useDateRangeFilterStore((state) => state.getApiDates(), shallow);
  const updateDateFilter = useDateRangeFilterStore((state) => state.updateDateFilter);

  const [user, setUser] = useState<UserUMS>();
  const [showFindUser, setShowFindUser] = useState(false);

  useEffect(() => {
    setUser(undefined);

    void useReportingUserStore
      .getState()
      .getUser(params.user)
      .then(setUser, (err: ResponseError) => {
        if ("status" in err && (err.status === 403 || err.status === 404)) {
          router.replace("/config/device/forbidden");
        } else {
          errorToast({
            title: "Please try again",
            description: "Failed to load student data. If the issue persists, please contact support.",
            isClosable: true,
          });
        }
      });

    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      GlobalDatePickerVisibilityActions.showGlobalDatePicker();
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.user]);

  const handleFindUserBtnClick = () => {
    setShowFindUser(true);
  };

  const handleHideFindUser = () => {
    setShowFindUser(false);
  };

  useEffect(() => {
    if (location?.query && location.query.startDate && location.query.endDate)
      updateDateFilter({
        startDate: location.query.startDate,
        endDate: location.query.endDate,
        startTime: location.query.startTime ? location.query.startTime : "00:00:00",
        endTime: location.query.endTime ? location.query.endTime : "23:59:59",
        relativeKey: "",
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.query]);

  const handlePushUserJourney = () => {
    router.push(`/surfwize/reporting/users/${params.user}/journey`);
  };

  const handlePushUserTimeline = () => {
    router.push(`/surfwize/reporting/users/${params.user}/timeline?endDate=${apiDateOverride.endDate}&endTime=${apiDateOverride.endTime}`);
  };

  if (!user) {
    return (
      <Flex minHeight="200px" alignItems="center" justifyContent="center">
        <Spinner size="md" />
      </Flex>
    );
  }

  return (
    <PageWithHeader
      title={t("User Dashboard")}
      breadcrumbs={[
        { title: t("Statistics"), url: "/surfwize/dashboard", isActive: false },
        { title: t("Users"), url: "/surfwize/reporting/users", isActive: false },
        { title: t("User Dashboard"), isActive: true },
      ]}
    >
      <Box m="sp24">
        <Flex justifyContent="space-between" alignItems="center" gap="sp16">
          <StudentNamePill user={user} />
          <Flex gap="sp16">
            <Button variant="primary" onClick={handlePushUserJourney}>
              {t("User Journey")}
            </Button>
            <Button variant="primary" onClick={handlePushUserTimeline}>
              {t("User Timeline")}
            </Button>
            <Button variant="outline" onClick={handleFindUserBtnClick}>
              <Icon icon="fa-search" boxSize="12px" mr="6px" aria-hidden="true" />
              {t("Find User")}
            </Button>
          </Flex>
        </Flex>
        <Grid templateColumns="repeat(12, 1fr)" gap="sp16" marginTop="sp24">
          <GraphCard user={user?.username} dateFilters={apiDateOverride} />
          <RedFlagsCard user={user?.username} dateFilters={apiDateOverride} />
        </Grid>
        <Grid templateColumns="repeat(24, 1fr)" gap="sp16" marginTop="sp24">
          <GridItem colSpan={8} background="neutrals.0" borderRadius="sm" height="fit-content">
            <TopTrafficTypesCard user={user.username} dateFilters={apiDateOverride} />
          </GridItem>
          <GridItem colSpan={5} borderRadius="sm">
            <FactsCardSet user={user} dateFilters={apiDateOverride} />
          </GridItem>
          <GridItem colSpan={11} borderRadius="sm">
            <EventTimelineCard user={user.username} dateFilters={apiDateOverride} />
          </GridItem>
        </Grid>
      </Box>
      <FindUserModal open={showFindUser} onClose={handleHideFindUser} />
    </PageWithHeader>
  );
};

export default UserDashboardIndex;
